import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import styled, { createGlobalStyle } from 'styled-components';
import { KYCProvider } from './contexts/KYCContext';
import { InventoryProvider } from './contexts/InventoryContext';
import { BlockchainProvider } from './contexts/BlockchainContext';
import { AlertProvider } from './contexts/AlertContext';
import { TokenizationProvider } from './contexts/TokenizationContext';
import { AssetClassProvider } from './contexts/AssetClassContext';
import { TokenIssuerProvider, useTokenIssuer } from './contexts/TokenIssuerContext';
import { ClientConfigProvider } from './contexts/ClientConfigContext';

// Import components
import Header from './components/Header/Header';
import Footer from './components/Footer/Footer';
import Dashboard from './components/Dashboard/Dashboard';
import CoreTokenizer from './components/CoreTokenizer/CoreTokenizer';
import OfflineTokenMintPage from './components/CoreTokenizer/OfflineTokenMintPage';
import LegalReviewCenter from './components/CoreTokenizer/LegalReviewCenter';
import LegalFirmAssets from './components/CoreTokenizer/LegalFirmAssets';
import TokenizerMyAssets from './components/CoreTokenizer/TokenizerMyAssets';
import TokenizerBulkAssetsRegistry from './components/CoreTokenizer/TokenizerBulkAssetsRegistry';
import AssetClassManager from './components/AssetClassManager/AssetClassManager';
import TokenIssuerManager from './components/TokenIssuerManager/TokenIssuerManager';
import IssuerDashboard from './components/IssuerDashboard/IssuerDashboard';
import Portfolio from './components/Portfolio/Portfolio';
import KYCVerification from './components/KYC/KYCVerification';
import TransparencyHub from './components/TransparencyHub/TransparencyHub';
import SugarTokenization from './components/SugarTokenization/SugarTokenization';
import AssetTracking from './components/AssetTracking/AssetTracking';

// Exchange-Side Features
import InventoryManagement from './components/InventoryManagement/InventoryManagement';
import CustodialOperations from './components/CustodialOperations/CustodialOperations';
import RealTimeMonitoring from './components/RealTimeMonitoring/RealTimeMonitoring';
import BlockchainExplorer from './components/BlockchainExplorer/BlockchainExplorer';
import WarehouseOperations from './components/WarehouseOperations/WarehouseOperations';
import QualityManagement from './components/QualityManagement/QualityManagement';
import ComplianceCenter from './components/ComplianceCenter/ComplianceCenter';
import ConversionCenter from './components/ConversionCenter/ConversionCenter';
import APIManagement from './components/APIManagement/APIManagement';
import AdminUserManagement from './components/Admin/AdminUserManagement';
import AdminLegalReviewFirms from './components/Admin/AdminLegalReviewFirms';
import AdminAssetClassTemplates from './components/Admin/AdminAssetClassTemplates';
import AdminAssetClasses from './components/Admin/AdminAssetClasses';
import AdminAssets from './components/Admin/AdminAssets';
import FuelInjection from './components/Admin/FuelInjection';
import UserAccount from './components/Account/UserAccount';

// Global styles
const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
    background-color: #0f0f23;
    color: #ffffff;
    line-height: 1.6;
  }

  button {
    cursor: pointer;
    border: none;
    outline: none;
  }

  input, select, textarea {
    outline: none;
  }

  /* Native <select> lists often render with a light background while inheriting body color (#fff), so options look invisible. */
  select {
    color-scheme: dark;
  }

  select option {
    background-color: #1a1a3e;
    color: #e8e8e8;
  }
`;

const AppContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #0f0f23 0%, #1a1a3e 100%);
  display: flex;
  flex-direction: column;
`;

const MainContent = styled.main`
  padding-top: 80px; // Account for fixed header
  flex: 1;
  min-height: calc(100vh - 80px);
`;

function HomeRoute() {
  const { isAuthorizedTokenizer } = useTokenIssuer();
  if (isAuthorizedTokenizer) {
    return <Navigate to="/tokenization" replace />;
  }
  return <Dashboard />;
}

function App() {
  return (
                  <AlertProvider>
                <ClientConfigProvider>
                <BlockchainProvider>
                  <TokenIssuerProvider>
                    <AssetClassProvider>
                      <TokenizationProvider>
                        <InventoryProvider>
                          <KYCProvider>
                <Router>
                  <AppContainer>
                    <GlobalStyle />
                    <Header />
                    <MainContent>
                      <Routes>
                        {/* User-Facing Features */}
                        <Route path="/" element={<HomeRoute />} />
                        <Route path="/dashboard" element={<Dashboard />} />
                                                        <Route path="/tokenization" element={<CoreTokenizer />} />
                        <Route path="/tokenization/offline-mint" element={<OfflineTokenMintPage />} />
                                <Route path="/legal-reviews" element={<LegalReviewCenter />} />
                                <Route path="/legal-firm/assets" element={<LegalFirmAssets />} />
                                <Route path="/my-assets" element={<TokenizerMyAssets />} />
                                <Route path="/bulk-assets-registry" element={<TokenizerBulkAssetsRegistry />} />
                                <Route path="/asset-classes" element={<AssetClassManager />} />
                                <Route path="/authorized-tokenizers" element={<TokenIssuerManager />} />
                                <Route path="/admin/users" element={<AdminUserManagement />} />
                                <Route path="/admin/legal-review-firms" element={<AdminLegalReviewFirms />} />
                        <Route path="/admin/asset-class-templates" element={<AdminAssetClassTemplates />} />
                        <Route path="/admin/asset-classes" element={<AdminAssetClasses />} />
                        <Route path="/admin/assets" element={<AdminAssets />} />
                                <Route path="/admin/bulk-assets-registry" element={<TokenizerBulkAssetsRegistry />} />
                                <Route path="/admin/fuel-injection" element={<FuelInjection />} />
                        <Route path="/admin/transfer-asset" element={<Navigate to="/admin/fuel-injection" replace />} />
                                <Route path="/account" element={<UserAccount />} />
                                <Route path="/token-issuers" element={<Navigate to="/authorized-tokenizers" replace />} />
                                <Route path="/issuer-dashboard" element={<IssuerDashboard />} />
                        <Route path="/portfolio" element={<Portfolio />} />
                        <Route path="/kyc" element={<KYCVerification />} />
                        <Route path="/transparency" element={<TransparencyHub />} />
                        <Route path="/sugar-tokenization" element={<SugarTokenization />} />
                        <Route path="/asset-tracking" element={<AssetTracking />} />
                        
                        {/* Exchange-Side Features */}
                        <Route path="/inventory-management" element={<InventoryManagement />} />
                        <Route path="/custodial-operations" element={<CustodialOperations />} />
                        <Route path="/real-time-monitoring" element={<RealTimeMonitoring />} />
                        <Route path="/blockchain-explorer" element={<BlockchainExplorer />} />
                        <Route path="/warehouse-operations" element={<WarehouseOperations />} />
                        <Route path="/quality-management" element={<QualityManagement />} />
                        <Route path="/compliance-center" element={<ComplianceCenter />} />
                        <Route path="/conversion-center" element={<ConversionCenter />} />
                        <Route path="/api-management" element={<APIManagement />} />
                      </Routes>
                    </MainContent>
                    <Footer />
                    <Toaster
                      position="top-right"
                      toastOptions={{
                        duration: 4000,
                        style: {
                          background: '#363636',
                          color: '#fff',
                          border: '1px solid #3d4de6',
                        },
                      }}
                    />
                  </AppContainer>
                </Router>
                                        </KYCProvider>
                        </InventoryProvider>
                      </TokenizationProvider>
                    </AssetClassProvider>
                  </TokenIssuerProvider>
                </BlockchainProvider>
                </ClientConfigProvider>
              </AlertProvider>
  );
}

export default App;
